import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, getDefaultRouteForUser, getUserAccessState } from './lib/auth';
import { isKioskDevice } from './lib/kiosk';
import locationService from './lib/location';
import notificationService from './lib/notifications';

// Layout & Core
import Layout from './components/layout/Layout';
import LoadingScreen from './components/ui/LoadingScreen';
import GPSPermissionBanner from './components/ui/GPSPermissionBanner';
import NotificationPermissionBanner from './components/ui/NotificationPermissionBanner';

// Standard Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import VenuesPage from './pages/VenuesPage';
import ChecklistsPage from './pages/ChecklistsPage';
import ChecklistBuilderPage from './pages/ChecklistBuilderPage';
import TasksPage from './pages/TasksPage';
import AuditsPage from './pages/AuditsPage';
import StaffHubPage from './pages/StaffHubPage';
import UsersPage from './pages/UsersPage';
import KioskPage from './pages/KioskPage';
import KioskScreenPage from './pages/KioskScreenPage';
import VenueIntelligencePage from './pages/VenueIntelligencePage';
import DepartmentsPage from './pages/DepartmentsPage';
import TrainingPage from './pages/TrainingPage';
import IncidentReportsPage from './pages/IncidentReportsPage';
import PerformanceInsightsPage from './pages/PerformanceInsightsPage';
import JoinPage from './pages/JoinPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import ClockInOutPage from './pages/ClockInOutPage';
import TimeAttendancePage from './pages/TimeAttendancePage';

// EHO-Shield Compliance Hub Pages
import ComplianceHubPage from './pages/ComplianceHubPage';
import DocumentVerificationPage from './pages/DocumentVerificationPage';
import KnowledgeProofingPage from './pages/KnowledgeProofingPage';
import RegulatoryModulesPage from './pages/RegulatoryModulesPage';
import EnterpriseMappingPage from './pages/EnterpriseMappingPage';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  const kioskMode = isKioskDevice();
  const accessState = getUserAccessState(user);

  if (kioskMode) return <Navigate to="/kiosk" replace />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (accessState === 'disabled') return <Navigate to="/account-disabled" replace />;
  if (accessState === 'pending_activation') return <Navigate to="/activate-account" replace />;
  
  return children;
};

const LoginRoute = ({ children }) => {
  const kioskMode = isKioskDevice();
  const { isAuthenticated, user } = useAuthStore();
  const accessState = getUserAccessState(user);

  if (kioskMode) return <Navigate to="/kiosk" replace />;
  if (isAuthenticated && accessState === 'active') {
    return <Navigate to={getDefaultRouteForUser(user)} replace />;
  }
  if (isAuthenticated && accessState === 'pending_activation') {
    return <Navigate to="/activate-account" replace />;
  }
  if (isAuthenticated && accessState === 'disabled') {
    return <Navigate to="/account-disabled" replace />;
  }
  return children;
};

const DisabledAccountPage = () => (
  <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
    <div className="max-w-md w-full card-rtb text-center">
      <h1 className="font-heading text-2xl font-bold text-white uppercase mb-3">Account Disabled</h1>
      <p className="text-[#A1A1AA] text-sm">
        Your account is currently disabled. Please contact your line manager or RTB support.
      </p>
    </div>
  </div>
);

function App() {
  const [appReady, setAppReady] = useState(false);
  const [gpsPermissionGranted, setGpsPermissionGranted] = useState(false);
  const [gpsChecked, setGpsChecked] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const hasSession = isAuthenticated;

  useEffect(() => {
    const timer = setTimeout(() => setAppReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (appReady && hasSession && !gpsChecked) checkGPSPermission();
  }, [appReady, hasSession, gpsChecked]);

  useEffect(() => {
    const ensurePushTokenSync = async () => {
      if (!appReady || !hasSession) return;
      if (!notificationService.isNative()) return;
      const hasPermission = await notificationService.hasPermission();
      if (!hasPermission) return;
      await notificationService.syncStoredTokenWithBackend();
    };
    ensurePushTokenSync().catch(console.error);
  }, [appReady, hasSession]);

  const checkGPSPermission = async () => {
    try {
      const hasPermission = await locationService.hasPermission();
      if (hasPermission) setGpsPermissionGranted(true);
      setGpsChecked(true);
    } catch (error) {
      console.error('Error checking GPS:', error);
      setGpsChecked(true);
    }
  };

  if (!appReady) return <LoadingScreen />;

  return (
    <div className="dark">
      {hasSession && <NotificationPermissionBanner />}
      {hasSession && !gpsPermissionGranted && (
        <GPSPermissionBanner 
          onPermissionGranted={() => setGpsPermissionGranted(true)}
          onPermissionDenied={() => setGpsPermissionGranted(false)}
        />
      )}

      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute><LoginPage /></LoginRoute>} />
          <Route path="/activate-account" element={<ActivateAccountPage />} />
          <Route path="/account-disabled" element={<DisabledAccountPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/kiosk" element={<KioskPage />} />
          
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="venues" element={<VenuesPage />} />
            <Route path="checklists" element={<ChecklistsPage />} />
            <Route path="checklist-builder" element={<ChecklistBuilderPage />} />
            <Route path="checklists/execute" element={<ChecklistsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="venue-intelligence" element={<VenueIntelligencePage />} />
            <Route path="audits" element={<AuditsPage />} />
            <Route path="staff-hub" element={<StaffHubPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="departments/:venueId" element={<DepartmentsPage />} />
            <Route path="training" element={<TrainingPage />} />
            <Route path="incidents" element={<IncidentReportsPage />} />
            <Route path="performance-insights" element={<PerformanceInsightsPage />} />
            <Route path="clock" element={<ClockInOutPage />} />
            <Route path="time-attendance" element={<TimeAttendancePage />} />
            <Route path="kiosk-screen" element={<KioskScreenPage />} />
            
            {/* RTB Phase 2: EHO-Shield Compliance Engine */}
            <Route path="compliance" element={<ComplianceHubPage />} />
            <Route path="compliance/documents" element={<DocumentVerificationPage />} />
            <Route path="compliance/knowledge" element={<KnowledgeProofingPage />} />
            <Route path="compliance/regulatory" element={<RegulatoryModulesPage />} />
            <Route path="compliance/enterprise" element={<EnterpriseMappingPage />} />
            <Route path="compliance/eho" element={<AuditsPage />} /> {/* Maps directly to Audits */}
            <Route path="compliance/staff" element={<StaffHubPage />} />
          </Route>
          
          <Route path="*" element={<Navigate to={isKioskDevice() ? '/kiosk' : '/login'} replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;